/**
 * طبقة «اسأل نطاق» — EXP-1.0 (غير منفّذة).
 *
 * في PHASE 1 لا يوجد نموذج لغوي ولا استدعاء AI. هذه الطبقة تعيد صياغة الحقول
 * الموجودة في ValuationResult فقط، ولا تضيف أي رقم من خارجها — وهو نفس القيد
 * المثبّت في Design Freeze على سلوك الـAI.
 *
 * أي إجابة تشير إلى المستقبل تُوسم `scenario: true` وتحمل تنبيهاً إلزامياً.
 */

import type { ValuationResult } from '@/domain/types';
import { MODEL_VERSIONS } from '@/domain/versions';
import { num, perSqm, pctAbs } from '@/lib/format';

export interface AskAnswer {
  question: string;
  answer: string;
  /** سيناريو افتراضي — يستوجب الوسم والتنبيه. */
  scenario: boolean;
  /** بطاقتا أرقام مستخرجة من النتيجة. */
  facts: { label: string; value: string }[];
  modelVersion: string;
}

export const PRESET_QUESTIONS = [
  'كم يجب أن ينخفض السعر ليصبح داخل النطاق؟',
  'كيف يقارن هذا الحي بحي مجاور؟',
  'ماذا لو احتفظت بالأرض ٣ سنوات؟',
  'من أين جاء نطاق القيمة العادلة؟',
] as const;

export const AI_SCENARIO_WARNING =
  'هذا سيناريو حسابي وليس توقعاً لسعر العقار أو توصية استثمارية.';

export function answerQuestion(question: string, r: ValuationResult): AskAnswer {
  const midPerSqm = Math.round((r.fair_value_low + r.fair_value_high) / 2 / r.area_sqm);
  const base = { question, modelVersion: MODEL_VERSIONS.EXP };

  if (question === PRESET_QUESTIONS[0]) {
    return {
      ...base,
      scenario: false,
      answer:
        r.difference_percentage <= 0
          ? `السعر المطلوب أصلاً داخل أو أقل من النطاق بـ${pctAbs(r.difference_percentage)}، فلا حاجة لتخفيض ليدخل النطاق.`
          : `ليدخل السعر منتصف النطاق يحتاج إلى انخفاض قدره ${pctAbs(r.difference_percentage)} تقريباً، أي إلى حدود ${num(midPerSqm * r.area_sqm)} ر.س.`,
      facts: [
        { label: 'سعر المتر المطلوب', value: perSqm(r.asking_price_per_sqm) },
        { label: 'منتصف النطاق', value: perSqm(midPerSqm) },
      ],
    };
  }

  if (question === PRESET_QUESTIONS[1]) {
    return {
      ...base,
      scenario: false,
      answer:
        'المقارنة بين الأحياء تعتمد على سعر التنفيذ وفجوة العرض/التنفيذ والسيولة. افتح صفحة الحي أو الخريطة لمقارنة الأحياء المجاورة على نفس المؤشرات.',
      facts: [
        { label: 'الحي', value: `${r.district} · ${r.city}` },
        { label: 'مؤشر الفرصة', value: `${r.opportunity_score} / 100` },
      ],
    };
  }

  if (question === PRESET_QUESTIONS[2]) {
    return {
      ...base,
      scenario: true,
      answer:
        'إذا استمرت وتيرة النمو الحالية دون تغيّر، فإن السيناريو الحسابي يشير إلى بقاء السعر ضمن مسار الحي نفسه خلال المدة المذكورة. هذا امتداد حسابي للبيانات الحالية فقط ولا يأخذ في الحسبان أي تغيّر تنظيمي أو عرض جديد.',
      facts: [
        { label: 'مؤشر الفرصة', value: `${r.opportunity_score} / 100` },
        { label: 'الثقة في التقييم', value: `${r.confidence_score}%` },
      ],
    };
  }

  if (question === PRESET_QUESTIONS[3]) {
    return {
      ...base,
      scenario: false,
      answer: `نطاق القيمة العادلة مبني على ${r.comparables_count} صفقة مماثلة في الحي، بعمر بيانات ${r.data_age} يوماً، بعد تعديل الفروق في المساحة والزمن. الثقة في التقييم ${r.confidence_score}% وتعكس كثافة هذه البيانات وتشابهها.`,
      facts: [
        { label: 'الصفقات المستخدمة', value: String(r.comparables_count) },
        { label: 'عمر البيانات', value: `${r.data_age} يوماً` },
      ],
    };
  }

  return {
    ...base,
    scenario: false,
    answer: `${r.headline} مؤشر الفرصة ${r.opportunity_score}/100 والثقة في التقييم ${r.confidence_score}%. لمزيد من التفصيل استخدم أحد الأسئلة الجاهزة أو افتح التحليل الكامل.`,
    facts: [
      { label: 'السعر المطلوب', value: perSqm(r.asking_price_per_sqm) },
      { label: 'نطاق القيمة العادلة', value: `${num(r.fair_value_low)}–${num(r.fair_value_high)} ر.س` },
    ],
  };
}
